import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from '../exchanges/components';
import { fold } from './api';

const MAX_RESULTS = 200;

/**
 * Liste déroulante avec recherche (questions, objets et champs Salesforce).
 * options : [{ value, label, detail, badge, disabled, disabledReason }]
 */
export default function SearchSelect({ label, options, value, onChange, placeholder = 'Rechercher…', emptyText = 'Aucun résultat.', disabled = false, help }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef(null);
  const input = useRef(null);
  const listId = useId();
  const labelId = useId();
  const selected = options.find((option) => option.value === value);

  const results = useMemo(() => {
    const words = fold(query).split(/\s+/).filter(Boolean);
    const matches = words.length
      ? options.filter((option) => {
        const haystack = fold(`${option.badge || ''} ${option.label} ${option.detail || ''} ${option.value}`);
        return words.every((word) => haystack.includes(word));
      })
      : options;
    return matches.slice(0, MAX_RESULTS);
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    input.current?.focus();
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  useEffect(() => { setActive(0); }, [query, open]);
  // Échap referme la liste sans fermer la fenêtre qui la contient : la fenêtre
  // écoute l'événement natif, avant le gestionnaire React.
  useEffect(() => {
    const node = input.current;
    if (!open || !node) return undefined;
    const stop = (event) => { if (event.key === 'Escape') event.stopPropagation(); };
    node.addEventListener('keydown', stop);
    return () => node.removeEventListener('keydown', stop);
  }, [open]);

  function choose(option) {
    if (!option || option.disabled) return;
    onChange(option.value, option);
    setOpen(false);
    setQuery('');
  }

  function keydown(event) {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((i) => Math.min(results.length - 1, i + 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (event.key === 'Enter') { event.preventDefault(); choose(results[active]); }
    else if (event.key === 'Escape') { event.preventDefault(); setOpen(false); }
  }

  return <div className="ad-select" ref={root}>
    <span className="ad-select-label" id={labelId}>{label}</span>
    <button type="button" className={`ad-select-trigger ${selected ? '' : 'is-empty'}`} disabled={disabled} aria-haspopup="listbox" aria-expanded={open} aria-labelledby={labelId} onClick={() => setOpen((v) => !v)}>
      {selected ? <span className="ad-option-text">{selected.badge && <span className="ad-code">{selected.badge}</span>}<span>{selected.label}{selected.detail && <small>{selected.detail}</small>}</span></span>
        : <span className="ad-placeholder">Choisir…</span>}
      <span aria-hidden="true" className="ad-select-caret">▾</span>
    </button>
    {help && <small className="ad-select-help">{help}</small>}
    {open && <div className="ad-select-popover">
      <label className="ad-select-search"><Icon name="search" size={16} /><span className="ex-sr-only">{label}</span>
        <input ref={input} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={keydown} placeholder={placeholder}
          role="combobox" aria-expanded="true" aria-controls={listId} aria-activedescendant={results[active] ? `${listId}-${active}` : undefined} />
      </label>
      <ul className="ad-select-list" role="listbox" id={listId} aria-labelledby={labelId}>
        {results.map((option, index) => <li key={option.value} id={`${listId}-${index}`} role="option" aria-selected={option.value === value} aria-disabled={option.disabled || undefined}
          className={`${index === active ? 'is-active' : ''} ${option.disabled ? 'is-disabled' : ''} ${option.value === value ? 'is-selected' : ''}`}
          onMouseEnter={() => setActive(index)} onMouseDown={(event) => { event.preventDefault(); choose(option); }}>
          {option.badge && <span className="ad-code">{option.badge}</span>}
          <span className="ad-option-main">{option.label}{option.detail && <small>{option.detail}</small>}{option.disabled && option.disabledReason && <small className="ad-option-reason">{option.disabledReason}</small>}</span>
          {option.value === value && <Icon name="check" size={16} />}
        </li>)}
        {!results.length && <li className="ad-select-empty">{emptyText}</li>}
        {results.length === MAX_RESULTS && <li className="ad-select-empty">Affinez la recherche pour voir d’autres résultats.</li>}
      </ul>
    </div>}
  </div>;
}
